import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CTable from "../../../components/Table/table";
import Select from "react-select";
import { brandPalette } from "../../../constants/brandPalette";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import {
  homeworkUncommentReportRequest,
} from "../../../modules/admin-modules/reports/Actions";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { CSVLink } from "react-csv";
import { getAllHomeworksRequest } from "../../../modules/admin-modules/homework/Actions";
import { RiDeleteBinLine, RiSearchLine } from "react-icons/ri";
import HomeworkComment from "../../../containers/admin/reports/homework-comment";
import HomeworkRefuseComment from "../../../containers/admin/reports/refuse-homework";
import { openAdminHomeworkCommentModel, openRefuseHomeworkModel } from "../../../modules/admin-modules/models/Actions";

const homeworkSelectBase = adminSelectStyles({ menuZIndex: 50 });
const homeworkSelectStyles = {
  ...homeworkSelectBase,
  control: (styles, state) => ({
    ...homeworkSelectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};

const HOUR_MS = 60 * 60 * 1000;
const TWELVE_H_MS = 12 * HOUR_MS;
const TWENTY_FOUR_H_MS = 24 * HOUR_MS;

/** @typedef {"fresh"|"aging"|"late"} TimingLevel */

const TIMING_LEVELS = {
  fresh: {
    id: "fresh",
    label: "< 12h",
    face: "😊",
    csvStatus: "On time (<12h)",
    bg: "rgba(56, 161, 105, 0.12)",
    color: "#276749",
  },
  aging: {
    id: "aging",
    label: "12–24h",
    face: "😕",
    csvStatus: "Aging (12–24h)",
    bg: "rgba(214, 158, 46, 0.14)",
    color: "#975A16",
  },
  late: {
    id: "late",
    label: "> 24h",
    face: "😠",
    csvStatus: "Late (>24h)",
    bg: "rgba(155, 44, 44, 0.12)",
    color: "#9B2C2C",
  },
};

function getSubmittedAt(row) {
  return row?.submitted_at ?? row?.created_at;
}

/** @returns {TimingLevel|null} */
function getSubmissionTimingLevel(row) {
  const submittedAt = getSubmittedAt(row);
  if (!submittedAt) return null;
  const ageMs = Date.now() - new Date(submittedAt).getTime();
  if (Number.isNaN(ageMs) || ageMs < 0) return "fresh";
  if (ageMs < TWELVE_H_MS) return "fresh";
  if (ageMs < TWENTY_FOUR_H_MS) return "aging";
  return "late";
}

function formatSubmittedAt(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  return date
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
    .replace(",", "");
}

function TimingBadge({ level }) {
  const meta = TIMING_LEVELS[level];
  if (!meta) return null;
  return (
    <Badge
      px={2}
      py={0.5}
      borderRadius="md"
      fontWeight="700"
      fontSize="sm"
      bg={meta.bg}
      color={meta.color}
      title={meta.label}
    >
      {meta.face} {meta.label}
    </Badge>
  );
}

const HomeworkUncommentReport = () => {
  const dispatch = useDispatch();
  const [homeworkId, setHomeworkId] = useState("");
  const [codeSearch, setCodeSearch] = useState("");
  /** @type {[Set<TimingLevel>, Function]} */
  const [timingFilters, setTimingFilters] = useState(() => new Set());

  const [offset, setPage] = useState(0);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (homeworkId) dispatch(homeworkUncommentReportRequest(homeworkId));
  }, [homeworkId, dispatch]);

  useEffect(() => {
    setCodeSearch("");
    setTimingFilters(new Set());
    setPageNumber(0);
    setPage(0);
  }, [homeworkId]);

  useEffect(() => {
    setPageNumber(0);
    setPage(0);
  }, [codeSearch, timingFilters]);

  useEffect(() => {
    dispatch(getAllHomeworksRequest());
  }, [dispatch]);

  const homeworks = useSelector((state) => state.homework.homeworks);
  const homeworkUncomment = useSelector(
    (state) => state.reports.homeworkUncomment
  );
  const isLoading = useSelector((state) => state.reports.isLoading);

  const homeworksOptions = homeworks?.map((data) => {
    return {
      value: data.id,
      label: `${data.name} (${data.unevaluated_count || 0})`,
    };
  });

  const timingCounts = useMemo(() => {
    const counts = { fresh: 0, aging: 0, late: 0 };
    if (!Array.isArray(homeworkUncomment)) return counts;
    homeworkUncomment.forEach((row) => {
      const level = getSubmissionTimingLevel(row);
      if (level) counts[level] += 1;
    });
    return counts;
  }, [homeworkUncomment]);

  const filteredSubmissions = useMemo(() => {
    if (!Array.isArray(homeworkUncomment)) return [];
    const query = codeSearch.trim().toLowerCase();
    const activeFilters = timingFilters;
    return homeworkUncomment
      .filter((row) => {
        const userName = String(row?.student?.user_name ?? "").toLowerCase();
        if (query && !userName.includes(query)) return false;
        if (activeFilters.size > 0) {
          const level = getSubmissionTimingLevel(row);
          if (!level || !activeFilters.has(level)) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(getSubmittedAt(a)) - new Date(getSubmittedAt(b)));
  }, [homeworkUncomment, codeSearch, timingFilters]);

  const toggleTimingFilter = (level) => {
    setTimingFilters((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  };

  const viewData = (data) => {
    setData(data);
  };
  const headers = [
    { label: "name", key: "name" },
    { label: "user_name", key: "user_name" },
    { label: "group", key: "group" },
    { label: "created_at", key: "created_at" },
    { label: "status", key: "status" },
  ];
  const Data = filteredSubmissions?.map((data, index) => {
    const level = getSubmissionTimingLevel(data);
    const meta = level ? TIMING_LEVELS[level] : null;
    return {
      ...data,
      index: `#${index + 1}`,
      id: data?.id,
      name: data?.student?.name,
      user_name: data?.student?.user_name,
      group: data?.student_group,
      created_at: formatSubmittedAt(getSubmittedAt(data)),
      status: meta ? meta.csvStatus : "",
      status_badge: level ? <TimingBadge level={level} /> : null,
      preview_answer: (
        <Button
          onClick={() => dispatch(openAdminHomeworkCommentModel())}
          size="sm"
          borderRadius="12px"
          bg={brandPalette.secondary}
          color="white"
          _hover={{ bg: brandPalette.primary }}
        >
          Review submission
        </Button>
      ),
      delete: (
        <IconButton
          aria-label="Reject submission"
          size="sm"
          borderRadius="12px"
          variant="ghost"
          icon={<RiDeleteBinLine color="red" />}
          onClick={() =>
            dispatch(
              openRefuseHomeworkModel()
            )
          }
        />
      ),
    };
  });
  const columns = [
    {
      Header: "#",
      accessor: "index",
    },
    {
      Header: "Student name",
      accessor: "name",
    },
    {
      Header: "Code",
      accessor: "user_name",
    },
    {
      Header: "Group",
      accessor: "group",
    },
    {
      Header: "Submitted at",
      accessor: "created_at",
    },
    {
      Header: "Timing",
      accessor: "status_badge",
    },
    {
      Header: " ",
      accessor: "preview_answer",
    },

    {
      Header: "  ",
      accessor: "delete",
    },
  ];

  const totalPage = Math.max(1, Math.ceil((Array.isArray(Data) ? Data.length : 0) / (size || 10)));

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={5}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "center" }}
          gap={3}
          flexWrap="wrap"
        >
          <Box>
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Homework submissions (Pending review)
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              😊 &lt;12h · 😕 12–24h · 😠 &gt;24h since submit (unchecked)
            </Text>
          </Box>

          <Flex align="center" gap={2} flexWrap="wrap" justifyContent={{ base: "flex-start", md: "flex-end" }}>
            <Box minW={{ base: "100%", md: "340px" }}>
              <Select
                options={homeworksOptions}
                isMulti={false}
                onChange={(d) => setHomeworkId(d?.value ?? "")}
                styles={homeworkSelectStyles}
                placeholder="Select homework"
              />
            </Box>

            <Box
              border="1px solid"
              borderColor="var(--border-subtle)"
              bg="var(--surface-muted)"
              borderRadius="14px"
              p="6px"
            >
              <CSVLink
                headers={headers}
                data={Array.isArray(Data) && Data.length !== 0 ? Data : []}
                filename={"homework-uncomment-report.csv"}
                target="_blank"
                style={{ textDecoration: "none" }}
              >
                <Button
                  size="sm"
                  borderRadius="12px"
                  bg="var(--surface-raised)"
                  border="1px solid"
                  borderColor="var(--border-subtle)"
                  _hover={{ bg: "var(--surface-muted)" }}
                  fontWeight="800"
                  color="var(--text-primary)"
                >
                  Download CSV
                </Button>
              </CSVLink>
            </Box>

            <LogoutMenu />
          </Flex>
        </Flex>

        <Box
          border="1px solid"
          borderColor="var(--border-subtle)"
          bg="var(--surface-raised)"
          borderRadius="18px"
          p={{ base: 4, md: 6 }}
          boxShadow="0 18px 55px rgba(0,0,0,0.12)"
        >
          <Flex
            mb={5}
            pb={5}
            gap={3}
            flexWrap="wrap"
            align={{ base: "stretch", md: "center" }}
            borderBottom="1px solid"
            borderColor="var(--border-subtle)"
          >
            <InputGroup flex={{ base: "1 1 100%", md: "1 1 240px" }} maxW={{ md: "320px" }}>
              <InputLeftElement pointerEvents="none" h="44px">
                <RiSearchLine color="var(--text-muted)" />
              </InputLeftElement>
              <Input
                value={codeSearch}
                onChange={(e) => setCodeSearch(e.target.value)}
                placeholder={
                  homeworkId ? "Search by student code" : "Select homework first"
                }
                isDisabled={!homeworkId}
                h="44px"
                pl="2.5rem"
                borderRadius="14px"
                bg="var(--surface-muted)"
                borderColor="var(--border-subtle)"
                color="var(--text-primary)"
                _placeholder={{ color: "var(--text-muted)" }}
                _focus={{
                  borderColor: brandPalette.primary,
                  boxShadow: "0 0 0 3px rgba(91, 13, 22, 0.12)",
                }}
                _disabled={{ opacity: 0.6, cursor: "not-allowed" }}
              />
            </InputGroup>

            <Flex
              align="center"
              gap={2}
              flexWrap="wrap"
              opacity={homeworkId ? 1 : 0.6}
            >
              {(["fresh", "aging", "late"]).map((level) => {
                const meta = TIMING_LEVELS[level];
                const active = timingFilters.has(level);
                const count = timingCounts[level];
                return (
                  <Button
                    key={level}
                    type="button"
                    size="sm"
                    h="44px"
                    px={3}
                    borderRadius="14px"
                    border="1px solid"
                    borderColor={active ? meta.color : "var(--border-subtle)"}
                    bg={active ? meta.bg : "var(--surface-muted)"}
                    color={active ? meta.color : "var(--text-primary)"}
                    fontWeight="800"
                    isDisabled={!homeworkId}
                    onClick={() => toggleTimingFilter(level)}
                    _hover={{
                      bg: meta.bg,
                      borderColor: meta.color,
                    }}
                  >
                    <Text as="span" fontSize="md" lineHeight={1} mr={1.5}>
                      {meta.face}
                    </Text>
                    {meta.label}
                    {homeworkId ? (
                      <Badge
                        ml={2}
                        px={1.5}
                        py={0.5}
                        borderRadius="md"
                        fontWeight="700"
                        fontSize="xs"
                        bg={active ? "whiteAlpha.800" : "blackAlpha.50"}
                        color={meta.color}
                      >
                        {count}
                      </Badge>
                    ) : null}
                  </Button>
                );
              })}
              {timingFilters.size > 0 ? (
                <Button
                  type="button"
                  size="sm"
                  h="44px"
                  variant="ghost"
                  fontWeight="700"
                  color="var(--text-muted)"
                  isDisabled={!homeworkId}
                  onClick={() => setTimingFilters(new Set())}
                >
                  Clear
                </Button>
              ) : null}
            </Flex>

            {homeworkId ? (
              <Text fontSize="sm" fontWeight="600" color="var(--text-muted)" alignSelf="center">
                {filteredSubmissions.length} of {homeworkUncomment?.length ?? 0} shown
              </Text>
            ) : (
              <Text fontSize="sm" fontWeight="600" color="var(--text-muted)" alignSelf="center">
                Select a homework above to load submissions
              </Text>
            )}
          </Flex>

          <CTable
            selectedData={viewData}
            Columns={columns}
            Data={Data}
            path={"openModel"}
            Actions
            Title={``}
            subTitle=""
            btnTitle=""
            placeHolder=""
            noSearchBar={true}
            noFilter={true}
            footerBtnTitle
            filterList
            addButton
            addButtonTitle=""
            setPage={setPage}
            setPerPage={setPerPage}
            currentpage={pageNumber}
            setPageNumber={setPageNumber}
            noSecondSearchBar={true}
            isLoading={isLoading}
            perPage={size}
            totalPage={totalPage}
          />
        </Box>
      </Box>
      <HomeworkComment data={data} homeworkId={homeworkId} />
      <HomeworkRefuseComment data={data} homeworkId={homeworkId} />
    </Layout>
  );
};

export default HomeworkUncommentReport;
